// WMS reports. Every one shares the same shell: filters → headline figures → table → CSV export.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, BarChart3, Boxes, Building2, Truck, IndianRupee, Maximize } from 'lucide-react';
import { api, API_BASE_URL, tokenStore } from '@/api/client';
import {
  Badge, WmsCard, WmsDateRange, WmsPageBar, WmsSearch,
  useActiveWarehouses, useSearchPaged, useWmsMerchants,
} from './WmsShared';

const inr = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const num = (v: any) => Number(v ?? 0).toLocaleString('en-IN');

/** Streams a report CSV with the caller's auth header (a plain <a> can't send one). */
async function downloadReport(kind: string, params: Record<string, any>) {
  const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== '' && v != null) as any).toString();
  const r = await fetch(`${API_BASE_URL}/wms/reports/${kind}/export${qs ? `?${qs}` : ''}`, {
    headers: { Authorization: `Bearer ${tokenStore.get()}` },
  });
  if (!r.ok) { alert((await r.text()).slice(0, 300) || 'Export failed'); return; }
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `wms-${kind}-report.csv`; a.click();
  URL.revokeObjectURL(url);
}

/** Filter bar shared by the reports. `dates` is off for reports that only describe "right now". */
function ReportFilters({ f, set, dates = true }: any) {
  const { data: warehouses = [] } = useActiveWarehouses();
  const { data: merchants = [] } = useWmsMerchants();
  return (
    <>
      <select value={f.merchantId ?? ''} onChange={(e) => set('merchantId', e.target.value)} className="border rounded px-2 py-1.5 text-sm">
        <option value="">Merchant — all</option>
        {(merchants as any[]).map((m) => <option key={m.id} value={m.id}>{m.companyName}</option>)}
      </select>
      <select value={f.warehouseId ?? ''} onChange={(e) => set('warehouseId', e.target.value)} className="border rounded px-2 py-1.5 text-sm">
        <option value="">Warehouse — all</option>
        {(warehouses as any[]).map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
      </select>
      {dates && <WmsDateRange from={f.from ?? ''} to={f.to ?? ''} onFrom={(v) => set('from', v)} onTo={(v) => set('to', v)} label="Period" />}
    </>
  );
}

function Stat({ label, value, tone = 'slate' }: { label: string; value: any; tone?: 'slate' | 'emerald' | 'red' | 'amber' }) {
  const tones = {
    slate: 'text-slate-800', emerald: 'text-emerald-700', red: 'text-red-600', amber: 'text-amber-600',
  } as const;
  return (
    <div className="bg-slate-50 border rounded-lg px-4 py-3 min-w-[130px]">
      <div className={`text-xl font-semibold ${tones[tone]}`}>{value}</div>
      <div className="text-xs text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}

function useReport(kind: string, f: Record<string, any>, enabled = true) {
  const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== '' && v != null));
  return useQuery({
    queryKey: ['wms-report', kind, params],
    enabled,
    queryFn: async () => (await api.get(`/wms/reports/${kind}`, { params })).data as any,
  });
}

function useFilters(initial: Record<string, any> = {}) {
  const [f, setF] = useState<Record<string, any>>(initial);
  return { f, set: (k: string, v: any) => setF((p) => ({ ...p, [k]: v })) };
}

// ─────────────────────────── 1. STOCK REPORT ───────────────────────────

export function StockReportTab() {
  const { f, set } = useFilters();
  const { data, isLoading } = useReport('stock', f);
  const items = data?.items ?? [];
  const t = data?.totals ?? {};
  const { q, setQ, page, setPage, total, pageRows } = useSearchPaged(
    items, (r: any, s) => [r.sku, r.name, r.merchantName, r.warehouseName, r.alert].some((v) => String(v ?? '').toLowerCase().includes(s)),
  );
  return (
    <WmsCard
      title={<span className="flex items-center gap-2"><Boxes size={16} /> Stock Report</span>}
      actions={<><WmsSearch value={q} onChange={setQ} placeholder="Search SKU / product" className="w-56" /><ReportFilters f={f} set={set} />
        <button onClick={() => downloadReport('stock', f)} className="border px-3 py-1.5 rounded text-sm text-slate-600 hover:bg-slate-50 flex items-center gap-1"><Download size={14} /> CSV</button></>}
    >
      <div className="flex gap-3 flex-wrap mb-4">
        <Stat label="SKUs" value={num(t.skus)} />
        <Stat label="Units on hand" value={num(t.units)} tone="emerald" />
        <Stat label="Stock value" value={inr(t.value)} />
        <Stat label="Damaged units" value={num(t.damaged)} tone="red" />
        <Stat label="Out of stock" value={num(t.outOfStock)} tone="red" />
        <Stat label="Low stock" value={num(t.lowStock)} tone="amber" />
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-emerald-50"><tr>
            <th className="p-2 text-left">SKU</th><th className="text-left">Product</th><th className="text-left">Merchant</th><th className="text-left">Warehouse</th>
            <th className="text-right">Available</th><th className="text-right">Damaged</th><th className="text-right">Value</th>
            <th className="text-right">In</th><th className="text-right">Out</th><th className="text-right">Returns</th><th>Alert</th>
          </tr></thead>
          <tbody>
            {isLoading ? <tr><td colSpan={11} className="text-center py-6 text-slate-400">Loading…</td></tr>
              : total === 0 ? <tr><td colSpan={11} className="text-center py-6 text-slate-400">No stock matches these filters</td></tr>
              : pageRows.map((r: any, i: number) => (
                <tr key={i} className="border-t">
                  <td className="p-2 font-mono text-xs">{r.sku}</td>
                  <td className="text-xs">{r.name}</td>
                  <td className="text-xs">{r.merchantName}</td>
                  <td className="text-xs">{r.warehouseName}</td>
                  <td className="text-right">{num(r.available)}</td>
                  <td className="text-right text-red-600">{r.damaged || '—'}</td>
                  <td className="text-right">{r.costPrice == null ? <span className="text-slate-400" title="No cost price on the product">—</span> : inr(r.stockValue)}</td>
                  <td className="text-right text-emerald-700">{r.inbound || '—'}</td>
                  <td className="text-right">{r.outbound || '—'}</td>
                  <td className="text-right">{r.returns || '—'}</td>
                  <td><Badge text={r.alert === 'IN_STOCK' ? 'GOOD' : r.alert === 'LOW_STOCK' ? 'PARTIAL' : 'REJECTED'} /></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <WmsPageBar page={page} total={total} onPage={setPage} />
    </WmsCard>
  );
}

// ─────────────────────────── 2. SPACE UTILIZATION ───────────────────────────

export function SpaceReportTab() {
  const { f, set } = useFilters();
  const { data, isLoading } = useReport('space', f);
  const items = data?.items ?? [];
  const t = data?.totals ?? {};
  return (
    <WmsCard
      title={<span className="flex items-center gap-2"><Maximize size={16} /> Space Utilization</span>}
      actions={<><ReportFilters f={f} set={set} dates={false} />
        <button onClick={() => downloadReport('space', f)} className="border px-3 py-1.5 rounded text-sm text-slate-600 hover:bg-slate-50 flex items-center gap-1"><Download size={14} /> CSV</button></>}
    >
      <div className="flex gap-3 flex-wrap mb-4">
        <Stat label="Warehouses" value={num(t.warehouses)} />
        <Stat label="Total capacity (sq ft)" value={num(t.capacitySqFt)} />
        <Stat label="Allocated (sq ft)" value={num(t.allocatedSqFt)} tone="emerald" />
        <Stat label="Overall utilization" value={t.utilizationPct == null ? '—' : `${t.utilizationPct}%`} tone={t.utilizationPct >= 90 ? 'red' : t.utilizationPct >= 70 ? 'amber' : 'emerald'} />
      </div>
      {isLoading ? <div className="text-center py-6 text-slate-400">Loading…</div>
        : items.length === 0 ? <div className="text-center py-6 text-slate-400">No warehouses</div>
        : <div className="space-y-4">
            {items.map((w: any) => (
              <div key={w.warehouseId} className="border rounded-lg p-4">
                <div className="flex items-center justify-between gap-4 flex-wrap mb-2">
                  <div className="font-medium">{w.warehouseName}</div>
                  <div className="text-sm text-slate-600">
                    {num(w.allocatedSqFt)} / {num(w.capacitySqFt)} sq ft
                    {w.utilizationPct == null
                      ? <span className="text-slate-400 ml-2">capacity not set</span>
                      : <span className={`ml-2 font-medium ${w.utilizationPct >= 90 ? 'text-red-600' : w.utilizationPct >= 70 ? 'text-amber-600' : 'text-emerald-600'}`}>{w.utilizationPct}% used</span>}
                    {w.overAllocated && <span className="ml-2 text-red-600 font-medium">over-allocated</span>}
                  </div>
                </div>
                {w.utilizationPct != null && (
                  <div className="h-2 bg-slate-100 rounded overflow-hidden mb-3">
                    <div className={`h-full ${w.utilizationPct >= 90 ? 'bg-red-500' : w.utilizationPct >= 70 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${Math.min(100, w.utilizationPct)}%` }} />
                  </div>
                )}
                {w.merchants.length === 0
                  ? <div className="text-xs text-slate-400">No merchant allocations at this warehouse.</div>
                  : <table className="min-w-full text-sm">
                      <thead className="text-xs text-slate-500"><tr><th className="text-left">Merchant</th><th className="text-right">Sq ft</th><th className="text-right">Share</th><th className="text-right">Units held</th><th className="text-right">Rent/sq ft/mo</th></tr></thead>
                      <tbody>{w.merchants.map((m: any) => (
                        <tr key={m.merchantId} className="border-t">
                          <td className="py-1">{m.merchantName}</td>
                          <td className="text-right">{num(m.allocatedSqFt)}</td>
                          <td className="text-right">{m.sharePct}%</td>
                          <td className="text-right">{num(m.units)}</td>
                          <td className="text-right">{m.rentPerSqFtPerMonth == null ? '—' : inr(m.rentPerSqFtPerMonth)}</td>
                        </tr>
                      ))}</tbody>
                    </table>}
              </div>
            ))}
          </div>}
    </WmsCard>
  );
}

// ─────────────────────────── 3. MERCHANT REPORT ───────────────────────────

export function MerchantReportTab() {
  const { f, set } = useFilters();
  const { data, isLoading } = useReport('merchant', f);
  const items = data?.items ?? [];
  const t = data?.totals ?? {};
  return (
    <WmsCard
      title={<span className="flex items-center gap-2"><Building2 size={16} /> Merchant Report</span>}
      actions={<><ReportFilters f={f} set={set} />
        <button onClick={() => downloadReport('merchant', f)} className="border px-3 py-1.5 rounded text-sm text-slate-600 hover:bg-slate-50 flex items-center gap-1"><Download size={14} /> CSV</button></>}
    >
      <div className="flex gap-3 flex-wrap mb-4">
        <Stat label="Merchants" value={num(t.merchants)} />
        <Stat label="Units held" value={num(t.units)} tone="emerald" />
        <Stat label="Stock value" value={inr(t.stockValue)} />
        <Stat label="Units inward" value={num(t.inbound)} />
        <Stat label="Units outward" value={num(t.outbound)} />
        <Stat label="Space (sq ft)" value={num(t.allocatedSqFt)} />
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-emerald-50"><tr>
            <th className="p-2 text-left">Merchant</th><th className="text-right">SKUs</th><th className="text-right">Units</th><th className="text-right">Damaged</th>
            <th className="text-right">Stock Value</th><th className="text-right">Inward</th><th className="text-right">Outward</th>
            <th className="text-right">Return units</th><th className="text-right">GRNs</th><th className="text-right">Returns</th><th className="text-right">Sq ft</th>
          </tr></thead>
          <tbody>
            {isLoading ? <tr><td colSpan={11} className="text-center py-6 text-slate-400">Loading…</td></tr>
              : items.length === 0 ? <tr><td colSpan={11} className="text-center py-6 text-slate-400">No WMS-enabled merchants match</td></tr>
              : items.map((r: any) => (
                <tr key={r.merchantId} className="border-t">
                  <td className="p-2">{r.merchantName}</td>
                  <td className="text-right">{num(r.skus)}</td>
                  <td className="text-right">{num(r.units)}</td>
                  <td className="text-right text-red-600">{r.damaged || '—'}</td>
                  <td className="text-right">{inr(r.stockValue)}</td>
                  <td className="text-right text-emerald-700">{num(r.inbound)}</td>
                  <td className="text-right">{num(r.outbound)}</td>
                  <td className="text-right">{num(r.returnsUnits)}</td>
                  <td className="text-right">{num(r.grnCount)}</td>
                  <td className="text-right">{num(r.returnCount)}</td>
                  <td className="text-right">{num(r.allocatedSqFt)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </WmsCard>
  );
}

// ─────────────────────────── 4. DISPATCH REPORT ───────────────────────────

export function DispatchReportTab() {
  const { f, set } = useFilters();
  const { data, isLoading } = useReport('dispatch', f);
  const t = data?.totals ?? {};
  const items = data?.items ?? [];
  const { q, setQ, page, setPage, total, pageRows } = useSearchPaged(
    items, (r: any, s) => [r.skuCode, r.skuName, r.merchantName, r.docketNumber, r.invoiceNumber, r.customerBillTo].some((v) => String(v ?? '').toLowerCase().includes(s)),
  );
  return (
    <WmsCard
      title={<span className="flex items-center gap-2"><Truck size={16} /> Dispatch Report</span>}
      actions={<><WmsSearch value={q} onChange={setQ} placeholder="Search SKU / docket / customer" className="w-56" /><ReportFilters f={f} set={set} />
        <button onClick={() => downloadReport('dispatch', f)} className="border px-3 py-1.5 rounded text-sm text-slate-600 hover:bg-slate-50 flex items-center gap-1"><Download size={14} /> CSV</button></>}
    >
      <div className="flex gap-3 flex-wrap mb-4">
        <Stat label="Dispatch lines" value={num(t.dispatches)} />
        <Stat label="Units dispatched" value={num(t.qty)} tone="emerald" />
      </div>
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <MiniTable title="By merchant" rows={(data?.byMerchant ?? []).slice(0, 8)} cols={[['merchantName', 'Merchant'], ['dispatches', 'Lines'], ['qty', 'Units']]} />
        <MiniTable title="By courier / mode" rows={(data?.byCourier ?? []).slice(0, 8)} cols={[['courier', 'Courier'], ['dispatches', 'Lines'], ['qty', 'Units']]} />
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-emerald-50"><tr>
            <th className="p-2 text-left">Date</th><th className="text-left">Merchant</th><th className="text-left">Warehouse</th>
            <th className="text-left">Docket / AWB</th><th className="text-left">Invoice</th><th className="text-left">SKU</th>
            <th className="text-right">Qty</th><th className="text-left">Bill To</th><th className="text-left">Mode</th>
          </tr></thead>
          <tbody>
            {isLoading ? <tr><td colSpan={9} className="text-center py-6 text-slate-400">Loading…</td></tr>
              : total === 0 ? <tr><td colSpan={9} className="text-center py-6 text-slate-400">No dispatches in this period</td></tr>
              : pageRows.map((r: any) => (
                <tr key={r.id} className="border-t">
                  <td className="p-2 text-xs text-slate-500">{r.dispatchDate ? String(r.dispatchDate).slice(0, 10) : '—'}</td>
                  <td className="text-xs">{r.merchantName}</td>
                  <td className="text-xs">{r.warehouseName}</td>
                  <td className="font-mono text-xs">{r.docketNumber ?? '—'}</td>
                  <td className="text-xs">{r.invoiceNumber ?? '—'}</td>
                  <td className="text-xs"><span className="font-mono">{r.skuCode ?? '—'}</span>{r.skuName ? ` · ${r.skuName}` : ''}</td>
                  <td className="text-right">{num(r.qtyDispatched)}</td>
                  <td className="text-xs">{r.customerBillTo ?? '—'}</td>
                  <td className="text-xs">{r.requestedMode ?? '—'}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <WmsPageBar page={page} total={total} onPage={setPage} />
    </WmsCard>
  );
}

function MiniTable({ title, rows, cols }: { title: string; rows: any[]; cols: [string, string][] }) {
  return (
    <div className="border rounded-lg p-3">
      <div className="text-sm font-medium mb-2">{title}</div>
      {rows.length === 0 ? <div className="text-xs text-slate-400">No data</div>
        : <table className="min-w-full text-sm">
            <thead className="text-xs text-slate-500"><tr>{cols.map(([, l], i) => <th key={l} className={i === 0 ? 'text-left' : 'text-right'}>{l}</th>)}</tr></thead>
            <tbody>{rows.map((r, i) => (
              <tr key={i} className="border-t">{cols.map(([k], j) => <td key={k} className={j === 0 ? 'py-1' : 'text-right'}>{j === 0 ? r[k] : num(r[k])}</td>)}</tr>
            ))}</tbody>
          </table>}
    </div>
  );
}

// ─────────────────────────── 5. BILLING REPORT ───────────────────────────

export function BillingReportTab() {
  // A bill is always for a period — default to the current month rather than erroring on first open.
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const { f, set } = useFilters({
    from: first.toISOString().slice(0, 10),
    to: now.toISOString().slice(0, 10),
    manDays: '',
  });
  const ready = !!f.from && !!f.to;
  const { data, isLoading, error } = useReport('billing', f, ready);
  const items = data?.items ?? [];
  const t = data?.totals ?? {};
  const [open, setOpen] = useState<number | null>(null);

  return (
    <WmsCard
      title={<span className="flex items-center gap-2"><IndianRupee size={16} /> Billing Report</span>}
      actions={<><ReportFilters f={f} set={set} />
        <input type="number" min={0} value={f.manDays ?? ''} onChange={(e) => set('manDays', e.target.value)} placeholder="Man-days" title="Man-days deployed in this period (drives the manpower charge)" className="border rounded px-2 py-1.5 text-sm w-28" />
        <button onClick={() => downloadReport('billing', f)} disabled={!ready} className="border px-3 py-1.5 rounded text-sm text-slate-600 hover:bg-slate-50 flex items-center gap-1 disabled:opacity-50"><Download size={14} /> CSV</button></>}
    >
      <p className="text-xs text-slate-500 mb-3">
        Prices each merchant&apos;s measured activity against their rate card (Masters → Merchants).
        A blank rate means <b>not charged</b> and contributes 0 — it is never guessed.
      </p>

      {!ready ? <div className="text-center py-6 text-slate-400">Pick a From and To date.</div>
        : error ? <div className="text-center py-6 text-red-600">{(error as any)?.response?.data?.message ?? 'Could not build the bill'}</div>
        : isLoading ? <div className="text-center py-6 text-slate-400">Loading…</div>
        : <>
            <div className="flex gap-3 flex-wrap mb-4">
              <Stat label="Merchants billed" value={num(t.merchants)} />
              <Stat label="Grand total" value={inr(t.grandTotal)} tone="emerald" />
              <Stat label="Space (sq ft)" value={num(t.allocatedSqFt)} />
              <Stat label="Months in period" value={data?.period?.months ?? '—'} />
            </div>
            {data?.note && <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-3 py-2 mb-3">{data.note}</div>}
            {items.length === 0 ? <div className="text-center py-6 text-slate-400">Nothing to bill for this period.</div>
              : <div className="space-y-3">
                  {items.map((m: any) => (
                    <div key={m.merchantId} className="border rounded-lg">
                      <button onClick={() => setOpen(open === m.merchantId ? null : m.merchantId)} className="w-full flex items-center justify-between gap-4 px-4 py-3 hover:bg-slate-50 text-left">
                        <div>
                          <div className="font-medium">{m.merchantName}</div>
                          <div className="text-xs text-slate-500">
                            {m.warehouses.join(', ') || '—'} · {num(m.allocatedSqFt)} sq ft · {num(m.unitsInward)} in / {num(m.unitsOutward)} out
                            {m.codShipments ? ` · ${num(m.codShipments)} COD` : ''}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-semibold text-emerald-700">{inr(m.total)}</div>
                          {m.unratedActivity.length > 0 && <div className="text-[11px] text-amber-600">{m.unratedActivity.length} charge(s) have no rate set</div>}
                        </div>
                      </button>
                      {open === m.merchantId && (
                        <div className="border-t px-4 py-3">
                          <table className="min-w-full text-sm">
                            <thead className="text-xs text-slate-500"><tr>
                              <th className="text-left">Charge</th><th className="text-right">Quantity</th><th className="text-left pl-2">Unit</th><th className="text-right">Rate</th><th className="text-right">Amount</th>
                            </tr></thead>
                            <tbody>
                              {m.lines.map((l: any, i: number) => (
                                <tr key={i} className={`border-t ${!l.charged && l.qty > 0 ? 'bg-amber-50' : ''}`}>
                                  <td className="py-1">{l.label}</td>
                                  <td className="text-right">{num(l.qty)}</td>
                                  <td className="pl-2 text-xs text-slate-500">{l.unit}</td>
                                  <td className="text-right">{l.rate == null ? <span className="text-amber-600 text-xs">no rate set</span> : inr(l.rate)}</td>
                                  <td className="text-right">{inr(l.amount)}</td>
                                </tr>
                              ))}
                              <tr className="border-t font-semibold"><td colSpan={4} className="py-1 text-right pr-3">Total</td><td className="text-right">{inr(m.total)}</td></tr>
                            </tbody>
                          </table>
                          {m.unratedActivity.length > 0 && (
                            <p className="text-xs text-amber-700 mt-2">
                              There was activity but no rate configured for: <b>{m.unratedActivity.join(', ')}</b>. Set those on Masters → Merchants if they should be charged.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>}
          </>}
    </WmsCard>
  );
}

export const REPORT_ICON = BarChart3;
